import * as React from "react";
import {faUserGroup} from "@fortawesome/free-solid-svg-icons";
import {PageTitle} from "../../components/TitleBar/PageTitle";
import EnhancedTable, {TableState} from "../../components/EnhancedTable/EnhancedTable";
import {deleteUser} from "../../utils/Api";
import {useSnackbar} from "notistack";
import Box from "@mui/material/Box";
import Modal from "../../components/Modal/Modal";
import UserInfoForm from "../../components/UserInfoForm/UserInfoForm";
import ConfirmationDialog from "../../components/ConfirmationDialog/ConfirmationDialog";
import {useCurrentUser, useUsersTableData} from "../../hooks/users";
import {HeadCellConfig} from "../../components/EnhancedTable/EnhancedTableHeaderRow";
import {CheckCircleTwoTone, CloseCircleTwoTone} from "@ant-design/icons";
import {useTheme} from "@mui/material";


export const UserContainer = () => {
    const [tableState, setTableState] = React.useState(({
        page: 1,
        pageSize: 10,
        orderBy: 'last_name',
        order: 'desc',
        searchString: null
    } as TableState));
    const [open, setOpen] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [userToDelete, setUserToDelete] = React.useState(null);
    const [selectedUserData, setSelectedUserData] = React.useState(null);

    const theme = useTheme();
    const {enqueueSnackbar} = useSnackbar();
    const currentUser = useCurrentUser();
    const userList = useUsersTableData(tableState);

    let hasError = currentUser.isError || userList.isError;
    let isLoading = currentUser.isLoading || userList.isLoading;
    let tableLoadingState: 'loading' | 'success' | 'error';
    if (hasError) {
        tableLoadingState = 'error';
    } else if (isLoading) {
        tableLoadingState = 'loading';
    } else {
        tableLoadingState = 'success';
    }

    const headerCfg: HeadCellConfig[] = [
        {
            key: "first_name",
            label: "First Name"
        },
        {
            key: "last_name",
            label: "Last Name"
        },
        {
            key: "email",
            label: "Email"
        },
        {
            key: "organization",
            label: "Organization",
            formatter: (row) => {

                return row.organization.name;
            }
        },
        {
            key: "verified",
            label: "Verified",
            formatter: (row) => {
                if (row.verified) {
                    return <Box sx={{ml: 2, fontSize: '1rem'}}>
                        <CheckCircleTwoTone twoToneColor={theme.palette.primary.main}/>
                    </Box>
                }
                return (
                    <Box sx={{ml: 2, fontSize: '1rem'}}>
                        <CloseCircleTwoTone twoToneColor={theme.palette.error.main}/>
                    </Box>
                );
            }
        }
    ];

    const closeModal = () => setOpen(false);

    const handleEditUser = async (user) => {
        setSelectedUserData(user)
        setOpen(true)
    }

    const handleDeleteConfirmation = (user) => {
        setUserToDelete(user);
        setConfirmOpen(true);
    }

    const confirmDelete = (confirmed: boolean) => {
        if (confirmed) {
            handleDeleteUser()
        }
        setConfirmOpen(false);
    }

    const handleDeleteUser = async () => {
        try {
            await deleteUser(userToDelete.id)

            enqueueSnackbar('User accounts deactivated.', {
                preventDuplicate: true,
                variant: 'success',
                anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
            });
            // userListUpdates(prev => prev + 1)
        } catch (e) {
            enqueueSnackbar('An error occurred, please try again.', {
                preventDuplicate: true,
                variant: 'error',
                anchorOrigin: {horizontal: 'right', vertical: 'bottom'}
            });
        }
    }

    const handleTableStateChange = (newState: TableState) => {
        setTableState(newState);
    }

    return (
        <Box>
            <PageTitle pageTitle="Users" icon={faUserGroup}/>
            <EnhancedTable
                loadingState={tableLoadingState}
                columns={headerCfg}
                rows={userList?.data?.results}
                rowCount={userList?.data?.count}
                tableState={tableState}
                canEdit={currentUser?.data?.is_staff}
                editHandler={handleEditUser}
                canDelete={currentUser?.data?.is_staff}
                deleteHandler={handleDeleteConfirmation}
                stateHandler={handleTableStateChange}
            />

            <Modal open={open} onClose={closeModal}>
                <UserInfoForm title="Update User" user={selectedUserData} submitCallback={closeModal}/>
            </Modal>

            <ConfirmationDialog
                open={confirmOpen}
                title={`Are you sure you want to delete ${userToDelete?.email}?`}
                body={`This will deactivate ${userToDelete?.email} and prevent them from logging in to the system.`}
                handleClose={confirmDelete}
            />
        </Box>
    )
}